import * as Sentry from '@sentry/react';
import { logger } from 'utils/logger';
import { RatchetResult } from 'features/e2ee/types';

export class SFrameTornDownError extends Error {
  constructor() {
    super('SFrame client is unavailable: torn down or never initialized');
    this.name = 'SFrameTornDownError';
  }
}

interface ReceiverState {
  ready: boolean;
  readyTimestamp?: number;
  pendingReady?: {
    resolve: () => void;
  };
}

const SFRAME_INIT_RETRY_DELAY_MS = 500;

export class SFrameClient {
  private _client?: any;

  private initialized: boolean = false;

  private lastRatchetCall?: number;

  private lastRatchetResult?: RatchetResult;

  private pendingInitialRatchet?: Promise<RatchetResult | undefined>;

  private receivers: Map<number, ReceiverState> = new Map();

  private initializeClient = async (senderId: number, encryptionKey: ArrayBuffer) => {
    // @ts-ignore
    const { SFrame } = await import('sframe');

    this._client = await SFrame.createClient(senderId, {
      skipVp8PayloadHeader: true,
    });

    this.setupEventListeners();

    await this.setSenderEncryptionKey(encryptionKey);

    this.initialized = true;

    logger.remote({ system: true, capture: 'e2ee' }).info('[E2EE] SFrame client initialized');
  };

  initialize = async (senderId: number, encryptionKey: ArrayBuffer) => {
    logger.remote({ system: true, capture: 'e2ee' }).info('[E2EE] Initializing SFrame client');

    try {
      await this.initializeClient(senderId, encryptionKey);
    } catch (firstError) {
      if (this._client) {
        this._client.close();
        this._client = undefined;
      }

      logger
        .remote({ system: true, capture: 'e2ee' })
        .warn('[E2EE] SFrame client initialization failed, retrying...', {
          error: firstError instanceof Error ? firstError.message : String(firstError),
        });

      await new Promise<void>((resolve) => {
        setTimeout(resolve, SFRAME_INIT_RETRY_DELAY_MS);
      });

      await this.initializeClient(senderId, encryptionKey);
    }
  };

  isInitialized = () => this.initialized;

  setSenderEncryptionKey = async (encryptionKey: ArrayBuffer) => {
    // Single-clause guard on purpose: initializeClient assigns _client before
    // setting initialized, and calls this method during that window.
    if (!this._client) {
      logger
        .remote({ system: true, capture: 'e2ee' })
        .warn('[E2EE] Sender key set skipped: client is gone');

      return;
    }

    await this._client.setSenderEncryptionKey(encryptionKey);

    this.lastRatchetCall = undefined;
    this.lastRatchetResult = undefined;
    this.pendingInitialRatchet = undefined;
  };

  addReceiver = async (senderId: number, encryptionKey: ArrayBuffer) => {
    const client = this._client;

    if (!this.initialized || !client) {
      throw new SFrameTornDownError();
    }

    this.receivers.set(senderId, { ready: false });

    await client.addReceiver(senderId);

    // cleanup() may have nulled or re-created the client while awaiting
    if (this._client !== client) {
      this.receivers.delete(senderId);
      throw new SFrameTornDownError();
    }

    await client.setReceiverEncryptionKey(senderId, encryptionKey);

    logger
      .remote({ system: true, capture: 'e2ee' })
      .debug(`[E2EE] Added receiver to SFrame`, { senderId });
  };

  waitForReceiverReady = (senderId: number, timeoutMs: number = 5000): Promise<void> => {
    if (!this.initialized || !this._client) {
      return Promise.reject(new SFrameTornDownError());
    }

    const receiver = this.receivers.get(senderId);

    if (receiver?.ready) {
      logger
        .remote({ system: true, capture: 'e2ee' })
        .debug(`[E2EE] Receiver already ready`, { senderId });

      return Promise.resolve();
    }

    if (!receiver) {
      logger
        .remote({ system: true, capture: 'e2ee' })
        .warn(`[E2EE] waitForReceiverReady called before addReceiver`, { senderId });

      return Promise.reject(new Error(`Receiver ${senderId} not found`));
    }

    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        logger
          .remote({ system: true, capture: 'e2ee' })
          .warn(`[E2EE] Receiver ready timeout`, { senderId, timeoutMs });
        receiver.pendingReady = undefined;
        resolve();
      }, timeoutMs);

      receiver.pendingReady = {
        resolve: () => {
          clearTimeout(timeoutId);
          receiver.pendingReady = undefined;
          resolve();
        },
      };
    });
  };

  isReceiverReady = (senderId: number): boolean => this.receivers.get(senderId)?.ready ?? false;

  getReceiverReadyTimestamp = (senderId: number): number | undefined =>
    this.receivers.get(senderId)?.readyTimestamp;

  deleteReceiver = async (senderId: number) => {
    if (!this.initialized || !this._client) {
      return;
    }

    await this._client.deleteReceiver(senderId);
    this.receivers.delete(senderId);
  };

  cleanup = () => {
    logger.remote({ system: true, capture: 'e2ee' }).info('[E2EE] Clearing SFrame client');

    this.initialized = false;
    this.lastRatchetCall = undefined;
    this.lastRatchetResult = undefined;
    this.pendingInitialRatchet = undefined;

    this.receivers.clear();

    if (this._client) {
      this._client.close();
      this._client = undefined;
    }
  };

  private guardTransform =
    (operation: 'encrypt' | 'decrypt') =>
    async (id: string, target: RTCRtpSender | RTCRtpReceiver): Promise<void> => {
      const remoteLog = logger.remote({ system: true, capture: 'e2ee' });

      if (!this._client) {
        remoteLog.warn(`[E2EE] SFrame ${operation} skipped: client is gone`, { id });
        return;
      }

      try {
        await this._client[operation](id, target);
      } catch (error) {
        const kind = target.track?.kind;
        const trackReadyState = target.track?.readyState;

        remoteLog.warn(`[E2EE] SFrame ${operation} transform failed`, {
          kind,
          trackReadyState,
          error: error instanceof Error ? error.message : String(error),
        });

        // Firefox rejects transform assignment while the connection is tearing
        // down (NS_ERROR_FAILURE); only failures on a live track are genuine.
        if (trackReadyState === 'live') {
          Sentry.captureException(error, { extra: { operation, id, kind } });
        }
      }
    };

  // janus calls encrypt/decrypt fire-and-forget, so rejections here would
  // escape as unhandled promise rejections.
  private janusClient = {
    encrypt: this.guardTransform('encrypt'),
    decrypt: this.guardTransform('decrypt'),
  };

  getClient = () => (this._client ? this.janusClient : undefined);

  ratchetSenderEncryptionKey = async (): Promise<RatchetResult | undefined> => {
    const client = this._client;

    if (!client) {
      logger
        .remote({ system: true, capture: 'e2ee' })
        .warn('[E2EE] Sender key ratchet skipped: client is gone');

      return;
    }

    // cleanup()/re-init swaps _client; whatever this call produces afterwards is stale
    const isStale = () => this._client !== client;

    const ratchet = async (timestamp: number) => {
      this.lastRatchetCall = timestamp;

      try {
        const result: RatchetResult = await client.ratchetSenderEncryptionKey();

        if (isStale()) {
          return;
        }

        this.lastRatchetResult = result;

        logger
          .remote({
            system: true,
            capture: 'e2ee',
          })
          .info('[E2EE] Sender stream key ratcheted');

        return result;
      } catch (error) {
        if (isStale()) {
          return;
        }

        throw error;
      }
    };
    const now = Date.now();

    if (!this.lastRatchetCall || !this.lastRatchetResult || now - this.lastRatchetCall > 1000) {
      if (!this.pendingInitialRatchet) {
        this.pendingInitialRatchet = ratchet(now);

        return this.pendingInitialRatchet;
      }

      if (!this.lastRatchetResult) {
        return this.pendingInitialRatchet;
      }

      return ratchet(now);
    }

    return this.lastRatchetResult;
  };

  private setupEventListeners = () => {
    this._client.addEventListener('receiverReady', (event: any) => {
      const { senderId } = event;
      const receiver = this.receivers.get(senderId);

      logger.remote({ system: true, capture: 'e2ee' }).info(`[E2EE] Receiver ready`, { senderId });

      if (receiver) {
        receiver.ready = true;
        receiver.readyTimestamp = Date.now();

        if (receiver.pendingReady) {
          receiver.pendingReady.resolve();
          receiver.pendingReady = undefined;
        }
      }
    });

    this._client.addEventListener('decryptFailed', (event: any) => {
      logger.remote({ system: true, capture: 'e2ee' }).warn(`[E2EE] Decryption failed`, {
        streamId: event.id,
        senderId: event.senderId,
      });
    });

    this._client.addEventListener('decryptionRestored', (event: any) => {
      logger.remote({ system: true, capture: 'e2ee' }).info(`[E2EE] Decryption restored`, {
        streamId: event.id,
        senderId: event.senderId,
      });
    });

    this._client.addEventListener('receiverNotFound', (event: any) => {
      logger
        .remote({ system: true, capture: 'e2ee' })
        .warn(`[E2EE] Receiver not found — frames arriving from unregistered sender`, {
          streamId: event.id,
          senderId: event.senderId,
        });
    });
  };
}

export const SFrameManager = new SFrameClient();
